/**
 * Cookie Consent Banner
 * ======================
 * GDPR-compliant cookie consent UI with customizable preferences
 */

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Switch } from '@/components/ui/switch';
import { 
  Cookie, Shield, BarChart3, Target, Sparkles 
} from 'lucide-react';
import {
  hasConsentChoice,
  getStoredConsent,
  acceptAllCookies,
  acceptNecessaryOnly,
  saveConsentPreferences,
  CONSENT_CATEGORIES,
} from '@/utils/consent';

// Category icons
const CATEGORY_ICONS = {
  necessary: Shield,
  analytics: BarChart3,
  marketing: Target,
  personalization: Sparkles,
};

// Routes where cookie consent is irrelevant (authenticated internal pages)
const SUPPRESSED_ROUTES = ['/corvana-crm', '/tech-admin', '/app/', '/app', '/staff-portal'];

const CookieConsentBanner = () => {
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState(() => {
    const stored = getStoredConsent();
    if (stored) {
      return {
        necessary: true,
        analytics: stored.analytics || false,
        marketing: stored.marketing || false,
        personalization: stored.personalization || false,
      };
    }
    return {
      necessary: true,
      analytics: false,
      marketing: false,
      personalization: false,
    };
  });

  useEffect(() => {
    // Never show on authenticated/internal routes
    const isSuppressed = SUPPRESSED_ROUTES.some(r => location.pathname.startsWith(r));
    if (isSuppressed) {
      setIsVisible(false);
      return;
    }
    // Check if user has already made a choice
    if (!hasConsentChoice()) {
      // Show promptly (but not jarringly) so visitors can opt out of analytics.
      const timer = setTimeout(() => setIsVisible(true), 2500);
      return () => clearTimeout(timer);
    }
  }, [location.pathname]);

  // Allow any "Cookie settings" trigger (e.g. footer link) to reopen the banner
  // with the preferences panel expanded, so a prior choice can be changed.
  useEffect(() => {
    const open = () => {
      const stored = getStoredConsent();
      if (stored) {
        setPreferences({
          necessary: true,
          analytics: stored.analytics || false,
          marketing: stored.marketing || false,
          personalization: stored.personalization || false,
        });
      }
      setShowPreferences(true);
      setIsVisible(true);
    };
    window.addEventListener('cv-open-consent', open);
    return () => window.removeEventListener('cv-open-consent', open);
  }, []);

  const handleAcceptAll = () => {
    acceptAllCookies();
    setIsVisible(false);
  };

  const handleAcceptNecessary = () => {
    acceptNecessaryOnly();
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    saveConsentPreferences(preferences);
    setIsVisible(false);
  };

  const handlePreferenceChange = (category, value) => {
    if (category === 'necessary') return; // Can't disable necessary cookies
    setPreferences(prev => ({ ...prev, [category]: value }));
  };

  return (
    <AnimatePresence>
      {isVisible && (
      <motion.div
        initial={{ y: 16, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 16, opacity: 0 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        className="cookie-consent-bar fixed z-[9999] bottom-4 left-4 right-4 sm:right-auto sm:bottom-5 sm:left-5 sm:w-[360px]"
      >
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-lg ring-1 ring-black/5 dark:ring-white/10 border border-slate-200/70 dark:border-slate-700/70 overflow-hidden">
          {/* Main bar — compact */}
          <div className="p-3.5">
            <div className="flex items-center gap-2 mb-1.5">
              <Cookie className="w-4 h-4 text-amber-500 flex-shrink-0" />
              <span className="text-[13px] font-semibold text-slate-900 dark:text-white">We value your privacy</span>
            </div>
            <p className="text-[12px] leading-relaxed text-slate-500 dark:text-slate-400 mb-3">
              We use first-party cookies to measure traffic and improve the site.{' '}
              <a href="/privacy" className="text-slate-700 dark:text-slate-300 underline underline-offset-2 hover:text-slate-900 dark:hover:text-white" target="_blank" rel="noopener noreferrer">
                Learn more
              </a>
            </p>
            {!showPreferences && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowPreferences(true)}
                  className="flex-1 h-8 rounded-lg text-[12px] font-medium text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                  data-testid="customize-cookies"
                >
                  Manage
                </button>
                <button
                  type="button"
                  onClick={handleAcceptAll}
                  className="flex-1 h-8 rounded-lg text-[12px] font-semibold text-white bg-primary hover:bg-primary/90 transition-colors"
                  data-testid="accept-all-cookies"
                >
                  Got it
                </button>
              </div>
            )}
          </div>

          {/* Preferences — compact, scroll-capped */}
          <AnimatePresence>
            {showPreferences && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="border-t border-slate-100 dark:border-slate-800 overflow-hidden"
              >
                <div className="p-3.5 bg-slate-50/60 dark:bg-slate-800/40 max-h-[46vh] overflow-y-auto">
                  {Object.entries(CONSENT_CATEGORIES).map(([key, category]) => {
                    const Icon = CATEGORY_ICONS[key];
                    const isEnabled = preferences[key];
                    return (
                      <div key={key} className="flex items-center gap-3 py-2 border-b border-slate-100 dark:border-slate-800 last:border-0">
                        <Icon className={`w-4 h-4 flex-shrink-0 ${isEnabled ? 'text-primary' : 'text-slate-400'}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[12px] font-medium text-slate-900 dark:text-white">{category.name}</span>
                            {category.required && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400">Always on</span>
                            )}
                          </div>
                          <p className="text-[11px] leading-snug text-slate-400 dark:text-slate-500 truncate">{category.description}</p>
                        </div>
                        <Switch
                          checked={isEnabled}
                          onCheckedChange={(checked) => handlePreferenceChange(key, checked)}
                          disabled={category.required}
                          className="flex-shrink-0 scale-90"
                          data-testid={`cookie-switch-${key}`}
                        />
                      </div>
                    );
                  })}
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      type="button"
                      onClick={handleAcceptNecessary}
                      className="flex-1 h-8 rounded-lg text-[12px] font-medium text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                      data-testid="accept-necessary-cookies"
                    >
                      Necessary only
                    </button>
                    <button
                      type="button"
                      onClick={handleSavePreferences}
                      className="flex-1 h-8 px-4 rounded-lg text-[12px] font-semibold text-white bg-primary hover:bg-primary/90 transition-colors"
                      data-testid="save-cookie-preferences"
                    >
                      Save preferences
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsentBanner;
